/**
 * The bucket-provisioning step's own submission state
 * (provision-sub-account-from-grant-dialog, design D5/D6).
 *
 * Deliberately **not** `submission.ts`: provisioning moves no money, carries no
 * `idempotency_key` (its idempotency is structural — get-or-create on
 * `(application_id, type_key)` and on `(account_id, type)`), and must not lock
 * the grant's fields. The shipped `Bonus Write Dialog Submission` contract
 * governs the amount-writing submissions alone; this machine governs the step
 * that may precede them.
 *
 * Invariants encoded here:
 * - at most one provisioning request in flight — a submit is rejected from
 *   `submitting`, from `confirming` (the post-2xx reload), and from
 *   `unconfirmed` (the reload answered, but not with a grantable bucket);
 * - the submitted `type_key` **and `asset_kind`** are captured at send time and
 *   every later decision uses those captured values, never the live selector;
 * - the dialog's `idempotencyKey` appears nowhere in this module: provisioning
 *   neither carries nor regenerates it.
 *
 * Immutable by construction: every transition returns a new state object.
 */

import type { AccountRow } from './accountsApi'

export type ProvisionStatus =
  | 'idle' // nothing sent
  | 'submitting' // a provisioning request is out
  | 'confirming' // 2xx received; the account detail reload is in flight
  | 'failed' // the provisioning itself failed; the step is retryable
  | 'unconfirmed' // provisioned, but the reload gave no grantable bucket
  | 'confirmed' // the reloaded row confirmed the bucket; grant form may open

export interface ProvisionState {
  status: ProvisionStatus
  /** The type captured when the request was sent — never re-read from the UI. */
  typeKey: string | null
  /** The asset kind captured at send time; half of the confirm predicate. */
  assetKind: string | null
  /** The server's own message, when there was one. */
  message: string | null
  /** Set only in `confirmed`: the bucket the grant form preselects. */
  subAccountId: string | null
}

/** The account-detail reload's outcome, as the dialog needs to tell them apart. */
export type DetailReloadResult =
  | { ok: true; row: AccountRow | null }
  | { ok: false; message: string }

export type ProvisionControl = 'provision' | 'cancel' | 'retry-the-refresh' | 'close'

export const initialProvisionState: ProvisionState = Object.freeze({
  status: 'idle',
  typeKey: null,
  assetKind: null,
  message: null,
  subAccountId: null,
})

/**
 * Whether a provisioning may be sent from `status`.
 *
 * `idle` and `failed` are the resting states: a failed provisioning must leave
 * the operator able to try the same type again or pick another one (spec:
 * "SHALL re-enable the type selection"). `submitting` and `confirming` are
 * in-flight, and `unconfirmed`/`confirmed` are post-provisioning states whose
 * only controls are the refresh retry and close.
 */
export function canSendProvision(status: ProvisionStatus): boolean {
  return status === 'idle' || status === 'failed'
}

export interface ProvisionSendResult {
  /** False when the transition was refused: no request may be sent. */
  ok: boolean
  state: ProvisionState
}

/**
 * Send: captures `typeKey` **and `assetKind`**, clears any previous message.
 * Rejects re-entry.
 */
export function beginProvision(
  state: ProvisionState,
  typeKey: string,
  assetKind: string
): ProvisionSendResult {
  if (!canSendProvision(state.status) || !typeKey || !assetKind) {
    return { ok: false, state }
  }
  return {
    ok: true,
    state: {
      status: 'submitting',
      typeKey,
      assetKind,
      message: null,
      subAccountId: null,
    },
  }
}

/** A 2xx: the bucket exists server-side; nothing is rendered from its body. */
export function beginConfirming(state: ProvisionState): ProvisionState {
  return { ...state, status: 'confirming', message: null, subAccountId: null }
}

/** The provisioning request itself failed — retryable, dialog stays open. */
export function failProvision(state: ProvisionState, message: string): ProvisionState {
  return { ...state, status: 'failed', message, subAccountId: null }
}

/** The reload confirmed a grantable bucket of the captured type. */
export function confirmProvision(
  state: ProvisionState,
  subAccountId: string
): ProvisionState {
  return { ...state, status: 'confirmed', message: null, subAccountId }
}

/** The reload failed, or carried no active bucket of the captured type. */
export function failConfirmation(
  state: ProvisionState,
  message: string
): ProvisionState {
  return { ...state, status: 'unconfirmed', message, subAccountId: null }
}

/** "Retry the refresh": another reload of the same captured type. */
export function retryConfirmation(state: ProvisionState): ProvisionSendResult {
  if (state.status !== 'unconfirmed' || !state.typeKey) return { ok: false, state }
  return { ok: true, state: { ...state, status: 'confirming', message: null } }
}

/** Exactly two controls after an unconfirmed outcome; the step's own otherwise. */
export function provisionControls(state: ProvisionState): readonly ProvisionControl[] {
  if (state.status === 'unconfirmed') return ['retry-the-refresh', 'close']
  return ['provision', 'cancel']
}

/**
 * The single post-provision predicate: status, type, and asset kind — each
 * compared against the value captured at send time (design D6; widened by
 * grant-dialog-monetary-bonus-target design D8).
 *
 * The asset kind is no longer the constant `'virtual'`: a confirmed MONETARY
 * provisioning must enter the grant form exactly as a virtual one does, or a
 * success would be rendered to the operator as 「该桶当前不可用于赠送」. It is
 * still *checked* rather than ignored — a reloaded bucket of the wrong kind is
 * not the bucket that was asked for.
 *
 * Returns the sub-account id only when the **reloaded** row carries an `active`
 * bucket of the captured type and captured asset kind, and `null` otherwise —
 * which covers both a failed reload (no row) and the get-or-create race that
 * returned a `frozen` or `closed` bucket. No balance is part of the predicate:
 * a bucket credited between its creation and the reload is still grantable.
 */
export function resolveProvisionedTarget(
  row: Pick<AccountRow, 'sub_accounts'> | null | undefined,
  typeKey: string | null,
  assetKind: string | null
): string | null {
  if (!row || !typeKey || !assetKind) return null
  const bucket = row.sub_accounts.find(
    (candidate) =>
      candidate.type === typeKey &&
      candidate.asset_kind === assetKind &&
      candidate.status === 'active'
  )
  return bucket ? bucket.id : null
}
